// Typed JSON-LD builders for SEO. Uses schema-dts for type safety.
import type {
  Organization,
  ProfessionalService,
  WebSite,
  Service,
  BreadcrumbList,
  FAQPage,
  Person,
  ContactPage,
  AboutPage,
  WithContext,
  Thing,
} from "schema-dts";
import {
  CONTACT_EMAIL,
  FOUNDER_NAME,
  LINKEDIN_URL,
  SITE_LOGO_IMAGE,
  SITE_NAME,
  SITE_OG_IMAGE,
  SITE_URL,
  siteContent,
} from "@/content/site";
import type { Locale } from "@/content/site";
import { absoluteLocalizedUrl, stripLocalePrefix } from "@/i18n/localePath";

export { FOUNDER_NAME, LINKEDIN_URL, SITE_NAME, SITE_URL };
export const FOUNDER_EMAIL = CONTACT_EMAIL;

const languageTag = (locale: Locale) => (locale === "fr" ? "fr-CA" : "en-CA");
const localizedSiteUrl = (path: string, locale: Locale) =>
  absoluteLocalizedUrl(SITE_URL, path, locale);

export const organizationJsonLd = (locale: Locale = "en"): WithContext<Organization> => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": `${SITE_URL}/#organization`,
  name: SITE_NAME,
  url: localizedSiteUrl("/", locale),
  logo: SITE_LOGO_IMAGE,
  sameAs: [LINKEDIN_URL],
  founder: {
    "@type": "Person",
    name: FOUNDER_NAME,
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      email: FOUNDER_EMAIL,
      contactType: "sales",
      areaServed: ["CA", "US"],
      availableLanguage: ["English", "French"],
    },
  ],
  areaServed: ["CA", "US"],
});

export const websiteJsonLd = (locale: Locale = "en"): WithContext<WebSite> => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": `${localizedSiteUrl("/", locale)}#website`,
  url: localizedSiteUrl("/", locale),
  name: SITE_NAME,
  inLanguage: languageTag(locale),
  publisher: { "@id": `${SITE_URL}/#organization` },
});

export const professionalServiceJsonLd = (locale: Locale = "en"): WithContext<ProfessionalService> => ({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  "@id": `${SITE_URL}/#service`,
  name: SITE_NAME,
  url: localizedSiteUrl("/", locale),
  image: SITE_OG_IMAGE,
  description: siteContent.brand.longPositioning[locale],
  areaServed: ["CA", "US"],
});

export const webPageJsonLd = (params: {
  url: string;
  name: string;
  description: string;
}): WithContext<Thing> => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${params.url}#webpage`,
  url: params.url,
  name: params.name,
  description: params.description,
  isPartOf: { "@id": `${SITE_URL}/#website` },
  publisher: { "@id": `${SITE_URL}/#organization` },
  inLanguage: ["en-CA", "fr-CA"],
}) as WithContext<Thing>;

export const collectionPageJsonLd = (params: {
  url: string;
  name: string;
  description: string;
  items: Array<{ name: string; path: string }>;
}): WithContext<Thing> => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "@id": `${params.url}#webpage`,
  url: params.url,
  name: params.name,
  description: params.description,
  isPartOf: { "@id": `${SITE_URL}/#website` },
  publisher: { "@id": `${SITE_URL}/#organization` },
  inLanguage: ["en-CA", "fr-CA"],
  mainEntity: {
    "@type": "ItemList",
    itemListElement: params.items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      url: `${SITE_URL}${item.path === "/" ? "" : item.path}`,
    })),
  },
}) as WithContext<Thing>;

export const breadcrumbJsonLd = (
  items: Array<{ name: string; path: string }>
): WithContext<BreadcrumbList> => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: item.name,
    item: `${SITE_URL}${item.path === "/" ? "" : item.path}`,
  })),
});

export const serviceJsonLd = (params: {
  name: string;
  description: string;
  url: string;
  serviceType?: string;
  audience?: string;
}): WithContext<Service> => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: params.name,
  description: params.description,
  url: params.url,
  serviceType: params.serviceType ?? params.name,
  provider: { "@id": `${SITE_URL}/#organization` },
  areaServed: ["CA", "US"],
  ...(params.audience
    ? {
        audience: {
          "@type": "Audience",
          audienceType: params.audience,
        },
      }
    : {}),
});

export const faqPageJsonLd = (
  items: Array<{ q: string; a: string }>
): WithContext<FAQPage> => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: items.map((item) => ({
    "@type": "Question",
    name: item.q,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.a,
    },
  })),
});

export const personJsonLd = (params: {
  name: string;
  jobTitle: string;
  url: string;
  sameAs?: string[];
}): WithContext<Person> => ({
  "@context": "https://schema.org",
  "@type": "Person",
  name: params.name,
  jobTitle: params.jobTitle,
  url: params.url,
  worksFor: { "@id": `${SITE_URL}/#organization` },
  ...(params.sameAs ? { sameAs: params.sameAs } : {}),
});

export const contactPageJsonLd = (params: {
  url: string;
  name: string;
  description: string;
}): WithContext<ContactPage> => ({
  "@context": "https://schema.org",
  "@type": "ContactPage",
  "@id": `${params.url}#webpage`,
  url: params.url,
  name: params.name,
  description: params.description,
  isPartOf: { "@id": `${SITE_URL}/#website` },
  publisher: { "@id": `${SITE_URL}/#organization` },
  mainEntity: { "@id": `${SITE_URL}/#organization` },
});

export const aboutPageJsonLd = (params: {
  url: string;
  name: string;
  description: string;
}): WithContext<AboutPage> => ({
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "@id": `${params.url}#webpage`,
  url: params.url,
  name: params.name,
  description: params.description,
  isPartOf: { "@id": `${SITE_URL}/#website` },
  publisher: { "@id": `${SITE_URL}/#organization` },
  mainEntity: { "@id": `${SITE_URL}/#organization` },
});

export function parseDisplayDate(display: string): string | undefined {
  const value = display.trim();
  // Search and Open Graph article dates require an exact ISO date or DateTime.
  // Month-only editorial labels stay visible but are not promoted as exact.
  return /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}(?::\d{2})?(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?)?$/.test(value)
    ? value
    : undefined;
}

export function parseReadMinutes(readTime: string): string {
  const match = readTime.match(/(\d+)/);
  const mins = match ? parseInt(match[1], 10) : 8;
  return `PT${mins}M`;
}

export const articleJsonLd = (params: {
  headline: string;
  description: string;
  datePublished: string; // display string like "March 2026"
  readTime: string;
  category: string;
  slug: string;
  locale: string;
  wordCount: number;
  authorName?: string;
  authorUrl?: string;
  keywords?: string;
  about?: string[];
}): WithContext<Thing> => {
  const exactDate = parseDisplayDate(params.datePublished);

  return ({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: params.headline,
    description: params.description,
    ...(exactDate ? { datePublished: exactDate, dateModified: exactDate } : {}),
    author: {
      "@type": "Person",
      name: params.authorName ?? FOUNDER_NAME,
      ...(params.authorUrl ? { url: params.authorUrl } : {}),
      worksFor: { "@id": `${SITE_URL}/#organization` },
    },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      url: SITE_OG_IMAGE,
    },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": `${SITE_URL}/insights/${params.slug}`,
  },
  articleSection: params.category,
  inLanguage: params.locale === "fr" ? "fr-CA" : "en-CA",
  wordCount: params.wordCount,
  timeRequired: parseReadMinutes(params.readTime),
  ...(params.keywords ? { keywords: params.keywords } : {}),
  ...(params.about && params.about.length
    ? {
        about: params.about.map((name) => ({
          "@type": "Thing",
          name,
        })),
      }
    : {}),
  }) as WithContext<Thing>;
};

export const authorityArticleJsonLd = (params: {
  headline: string;
  description: string;
  url: string;
  category: string;
  locale: string;
  readTime: string;
  keywords?: string;
}): WithContext<Thing> => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: params.headline,
  description: params.description,
  author: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
  },
  publisher: {
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      url: SITE_OG_IMAGE,
    },
  },
  mainEntityOfPage: {
    "@type": "WebPage",
    "@id": params.url,
  },
  articleSection: params.category,
  inLanguage: params.locale === "fr" ? "fr-CA" : "en-CA",
  timeRequired: parseReadMinutes(params.readTime),
  ...(params.keywords ? { keywords: params.keywords } : {}),
}) as WithContext<Thing>;

const ASSET_PATH = /\.(?:avif|gif|jpe?g|png|svg|webp|ico|xml|txt)$/i;

const localizeStructuredUrl = (value: string, locale: Locale) => {
  if (!value.startsWith(SITE_URL)) return value;

  const parsed = new URL(value);
  if (ASSET_PATH.test(parsed.pathname)) return value;

  // These identifiers describe the global organization rather than a
  // language-specific page and stay stable across locales.
  if (parsed.pathname === "/" && ["#organization", "#service"].includes(parsed.hash)) {
    return value;
  }

  const localized = absoluteLocalizedUrl(SITE_URL, stripLocalePrefix(parsed.pathname), locale);
  return `${localized}${parsed.search}${parsed.hash}`;
};

/**
 * Localize URLs and language declarations in page-level structured data.
 * Page builders remain reusable while the active `/fr` route is represented
 * as its own document rather than as bilingual content on one URL.
 */
export const localizeJsonLd = (value: unknown, locale: Locale): unknown => {
  if (typeof value === "string") {
    return value.startsWith(SITE_URL) ? localizeStructuredUrl(value, locale) : value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => localizeJsonLd(item, locale));
  }

  if (!value || typeof value !== "object") return value;

  const localized = Object.fromEntries(
    Object.entries(value as Record<string, unknown>).map(([key, item]) => {
      if (key === "inLanguage") return [key, languageTag(locale)];
      return [key, localizeJsonLd(item, locale)];
    }),
  );

  const schemaType = localized["@type"];
  if (
    typeof schemaType === "string"
    && ["WebPage", "CollectionPage", "Article", "AboutPage", "ContactPage", "WebSite"].includes(schemaType)
  ) {
    localized.inLanguage = languageTag(locale);
  }

  return localized;
};
